/**
 * HustleMeetRatePage — /meet/rate/:eventId
 *
 * Sprint 2 (g): post-event 5-star rating capture.
 * Posts to `/api/hustlemeet/rating` and shows a thank-you card.
 */
import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Star, CheckCircle2, Send } from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function HustleMeetRatePage() {
  const { eventId } = useParams();
  const [params] = useSearchParams();
  const [stars, setStars] = useState(0);
  const [email, setEmail] = useState(params.get("email") || "");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const hostId = params.get("host") || null;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!stars || stars < 1) return setErr("Pick a star rating first.");
    if (!email.includes("@")) return setErr("Enter your email.");
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/hustlemeet/rating`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          event_id: eventId, host_id: hostId,
          email, stars, comment,
        }),
      });
      if (!r.ok) throw new Error("save failed");
      setDone(true);
    } catch (_e) {
      setErr("Could not save your rating. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="hm-rate-page" className="min-h-screen bg-stone-50 dark:bg-[#0a0908]">
      <PlatformHeader brand={{ name: "HustleMeet", to: "/meet" }} showBookCta={false} showCart={false} />
      <div className="max-w-lg mx-auto px-6 py-14">
        {done ? (
          <div data-testid="hm-rate-done" className="text-center bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-3xl p-10 shadow-sm">
            <CheckCircle2 size={44} className="text-amber-500 mx-auto mb-4" />
            <h1 className="text-2xl font-black mb-2 text-stone-900 dark:text-stone-100">Thanks for the {stars}-star review</h1>
            <p className="text-sm text-stone-600 dark:text-stone-400">
              We'll share honest ratings publicly on the host leaderboard.
              You can rate every HustleMeet you attend — see you at the next one.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-3xl p-8 shadow-sm">
            <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">Rate your event</p>
            <h1 className="mt-2 text-2xl md:text-3xl font-black text-stone-900 dark:text-stone-100">
              How was it?
            </h1>
            <p className="mt-2 text-sm text-stone-600 dark:text-stone-400">
              Your rating helps future guests find great hosts. Event
              <code className="mx-1 text-amber-600">{eventId}</code>.
            </p>

            <div className="mt-6 flex items-center gap-2" role="radiogroup" aria-label="Star rating">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n} type="button"
                  onClick={() => setStars(n)}
                  data-testid={`hm-rate-star-${n}`}
                  aria-label={`${n} star${n === 1 ? "" : "s"}`}
                  className="p-1"
                >
                  <Star
                    size={38}
                    className={n <= stars ? "text-amber-500 fill-amber-500" : "text-stone-300 dark:text-stone-700"}
                  />
                </button>
              ))}
            </div>

            <label className="mt-6 text-[11px] uppercase tracking-widest font-bold text-stone-500 block mb-1">
              Your email
            </label>
            <input
              type="email" required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="hm-rate-email"
              className="w-full px-4 py-3 rounded-lg bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 text-stone-900 dark:text-stone-100 focus:outline-none focus:border-amber-400"
              placeholder="you@example.com"
            />

            <label className="mt-4 text-[11px] uppercase tracking-widest font-bold text-stone-500 block mb-1">
              What did you think? <span className="text-stone-400 normal-case font-normal">(optional)</span>
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              data-testid="hm-rate-comment"
              rows={3}
              maxLength={1000}
              placeholder="The food was incredible and the host kept the vibe going all night…"
              className="w-full px-4 py-3 rounded-lg bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 text-stone-900 dark:text-stone-100 focus:outline-none focus:border-amber-400"
            />

            {err && (
              <p data-testid="hm-rate-err" className="mt-3 text-sm text-rose-600">{err}</p>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="hm-rate-submit"
              className="w-full mt-6 px-5 py-3 rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
            >
              {busy ? "Sending…" : (<><Send size={16}/> Submit rating</>)}
            </button>
          </form>
        )}
      </div>
      <SuiteFooter />
    </div>
  );
}
